"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var bignumber_js_1 = __importDefault(require("bignumber.js"));
//@ts-ignore
var blakejs_1 = require("blakejs");
var ed25519_1 = __importDefault(require("./ed25519"));
var nano_address_1 = __importDefault(require("./nano-address"));
var nano_converter_1 = __importDefault(require("./nano-converter"));
var signer_1 = __importDefault(require("./signer"));
var convert_1 = __importDefault(require("./util/convert"));
var BlockSigner = /** @class */ (function () {
    function BlockSigner() {
        this.nanoAddress = new nano_address_1.default();
        this.ed25519 = new ed25519_1.default();
        this.signer = new signer_1.default();
        this.preamble = 0x6.toString().padStart(64, '0');
    }
    /**
     * Sign a receive block
     *
     * @param {ReceiveBlock} data The data required to sign a receive block
     * @param {string} privateKey Private key to sign the data with
     * @returns {SignedBlock} the signed block to publish to the blockchain
     */
    BlockSigner.prototype.receive = function (data, privateKey) {
        var validateInputRaw = function (input) { return !!input && !isNaN(+input); };
        if (!validateInputRaw(data.walletBalanceRaw)) {
            throw new Error('Invalid format in wallet balance');
        }
        if (!validateInputRaw(data.amountRaw)) {
            throw new Error('Invalid format in send amount');
        }
        if (!this.nanoAddress.validateNanoAddress(data.toAddress)) {
            throw new Error('Invalid toAddress');
        }
        if (!this.nanoAddress.validateNanoAddress(data.representativeAddress)) {
            throw new Error('Invalid representativeAddress');
        }
        if (!data.transactionHash) {
            throw new Error('No transaction hash');
        }
        if (!data.frontier) {
            throw new Error('No frontier');
        }
        if (!data.work) {
            throw new Error('No work');
        }
        if (!privateKey) {
            throw new Error('Please input the private key to sign the block');
        }
        var balanceNano = nano_converter_1.default.convert(data.walletBalanceRaw, 'RAW', 'NANO');
        var amountNano = nano_converter_1.default.convert(data.amountRaw, 'RAW', 'NANO');
        var newBalanceNano = new bignumber_js_1.default(balanceNano).plus(new bignumber_js_1.default(amountNano));
        var newBalanceRaw = nano_converter_1.default.convert(newBalanceNano, 'NANO', 'RAW');
        var newBalanceHex = convert_1.default.dec2hex(newBalanceRaw, 16).toUpperCase();
        var account = this.nanoAddressToHexString(data.toAddress);
        var link = data.transactionHash;
        var representative = this.nanoAddressToHexString(data.representativeAddress);
        var signature = this.signer.sign(privateKey, this.preamble, account, data.frontier, representative, newBalanceHex, link);
        return {
            type: 'state',
            account: data.toAddress,
            previous: data.frontier,
            representative: data.representativeAddress,
            balance: newBalanceRaw,
            link: link,
            signature: signature,
            work: data.work,
        };
    };
    /**
     * Sign a send block
     *
     * @param {SendBlock} data The data required to sign a send block
     * @param {string} privateKey Private key to sign the data with
     * @returns {SignedBlock} the signed block to publish to the blockchain
     */
    BlockSigner.prototype.send = function (data, privateKey) {
        var validateInputRaw = function (input) { return !!input && !isNaN(+input); };
        if (!validateInputRaw(data.walletBalanceRaw)) {
            throw new Error('Invalid format in wallet balance');
        }
        if (!validateInputRaw(data.amountRaw)) {
            throw new Error('Invalid format in send amount');
        }
        if (!this.nanoAddress.validateNanoAddress(data.toAddress)) {
            throw new Error('Invalid toAddress');
        }
        if (!this.nanoAddress.validateNanoAddress(data.fromAddress)) {
            throw new Error('Invalid fromAddress');
        }
        if (!this.nanoAddress.validateNanoAddress(data.representativeAddress)) {
            throw new Error('Invalid representativeAddress');
        }
        if (!data.frontier) {
            throw new Error('Frontier is not set');
        }
        if (!data.work) {
            throw new Error('Work is not set');
        }
        if (!privateKey) {
            throw new Error('Please input the private key to sign the block');
        }
        var balanceNano = nano_converter_1.default.convert(data.walletBalanceRaw, 'RAW', 'NANO');
        var amountNano = nano_converter_1.default.convert(data.amountRaw, 'RAW', 'NANO');
        var newBalanceNano = new bignumber_js_1.default(balanceNano).minus(new bignumber_js_1.default(amountNano));
        var newBalanceRaw = nano_converter_1.default.convert(newBalanceNano, 'NANO', 'RAW');
        var newBalanceHex = convert_1.default.dec2hex(newBalanceRaw, 16).toUpperCase();
        var account = this.nanoAddressToHexString(data.fromAddress);
        var link = this.nanoAddressToHexString(data.toAddress);
        var representative = this.nanoAddressToHexString(data.representativeAddress);
        var signature = this.signer.sign(privateKey, this.preamble, account, data.frontier, representative, newBalanceHex, link);
        return {
            type: 'state',
            account: data.fromAddress,
            previous: data.frontier,
            representative: data.representativeAddress,
            balance: newBalanceRaw,
            link: link,
            signature: signature,
            work: data.work,
        };
    };
    BlockSigner.prototype.nanoAddressToHexString = function (addr) {
        addr = addr.slice(-60);
        var isValid = /^[13456789abcdefghijkmnopqrstuwxyz]+$/.test(addr);
        if (isValid) {
            var keyBytes = this.nanoAddress.decodeNanoBase32(addr.substring(0, 52));
            var hashBytes = this.nanoAddress.decodeNanoBase32(addr.substring(52, 60));
            var blakeHash = blakejs_1.blake2b(keyBytes, undefined, 5).reverse();
            if (convert_1.default.ab2hex(hashBytes) == convert_1.default.ab2hex(blakeHash)) {
                var key = convert_1.default.ab2hex(keyBytes).toUpperCase();
                return key;
            }
            throw new Error('Checksum mismatch in address');
        }
        else {
            throw new Error('Illegal characters in address');
        }
    };
    return BlockSigner;
}());
exports.default = BlockSigner;
